import {
  DEMO_ACTIVE_EDGES_MINUTES,
  DEMO_END_REASONS,
  DEMO_LIFETIME_EDGES_HOURS,
  DEMO_OPEN_EDGES_MINUTES,
  DEMO_STEPS,
  DEMO_TIERS,
  DEMO_VISIT_EDGES,
  type DemoEndReason,
  type DemoSummary,
  share,
} from "@bt/domain/report";
import { useQuery } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { ArrowRightIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { navigate } from "@/lib/pages";
import { BarList } from "../charts/bar-list";
import { ChartFrame } from "../charts/frame";
import { type HeatRow, HeatTable } from "../charts/heat-table";
import { TimeSeries } from "../charts/time-series";
import { useAdminFormat } from "../format";
import { QueryView } from "../query-view";
import { type CoreOutputs, useTRPC } from "../trpc";
import { useView } from "../view";
import {
  areaLabel,
  BucketsFigure,
  CountsFigure,
  Grid,
  HistoryFigure,
  ReportView,
  Section,
  Stats,
  TallyFigure,
} from "./kit";

type Outcomes = CoreOutputs["demo"]["outcomes"];

/** Does the demo convince? (figures.md › Demo): the demo outcomes over the range. */
export function DemoPage() {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const { installation, range } = useView();
  const query = useQuery(trpc.demo.outcomes.queryOptions({ name: installation, range }));
  return (
    <QueryView query={query}>
      {(data) => (
        <div className="space-y-8">
          <Section title={t("adminApp.demo.wayIn")} description={t("adminApp.demo.wayInHint")}>
            <WayIn data={data} />
          </Section>
          <Section
            title={t("adminApp.demo.engagement")}
            description={t("adminApp.demo.engagementHint")}
          >
            <Engagement data={data} />
          </Section>
          <Section title={t("adminApp.demo.time")} description={t("adminApp.demo.timeHint")}>
            <Time summary={data.summary} />
          </Section>
          <Section title={t("adminApp.demo.ended")}>
            <Ended data={data} />
          </Section>
          <Section title={t("adminApp.demo.played")} description={t("adminApp.demo.playedHint")}>
            <Played summary={data.summary} />
          </Section>
          <Section
            title={t("adminApp.demo.capacity")}
            description={t("adminApp.demo.capacityHint")}
          >
            <Capacity />
          </Section>
        </div>
      )}
    </QueryView>
  );
}

/** Each step of the way in against the one before: the share that went on, and the drop-off. */
function useSteps() {
  const { t } = useTranslation();
  const format = useAdminFormat();
  return (summary: DemoSummary) =>
    DEMO_STEPS.map((step, i) => {
      const value = summary.steps[step];
      const before = i === 0 ? null : summary.steps[DEMO_STEPS[i - 1] ?? "requested"];
      return {
        key: step,
        label: t(`adminApp.demo.steps.${step}`),
        value,
        detail:
          before === null
            ? undefined
            : t("adminApp.demo.ofStepBefore", { share: format.share(share(value, before)) }),
      };
    });
}

function WayIn({ data }: { data: Outcomes }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const steps = useSteps()(data.summary);
  const neverOpened = data.endedBy.find((e) => e.value === "link_expired")?.count ?? 0;
  return (
    <div className="space-y-4">
      <Stats
        items={[
          {
            key: "requested",
            label: t("adminApp.demo.steps.requested"),
            value: format.count(data.total),
          },
          {
            key: "neverOpened",
            label: t("adminApp.demo.neverOpened"),
            value: format.count(neverOpened),
            detail: t("adminApp.demo.neverOpenedHint"),
          },
          {
            key: "reset",
            label: t("adminApp.demo.resets"),
            value: format.count(data.summary.reset.demos),
            detail: t("adminApp.demo.resetsHint", { count: data.summary.reset.resets }),
          },
        ]}
      />
      <Grid>
        <ChartFrame
          title={t("adminApp.demo.funnel")}
          description={t("adminApp.demo.funnelHint")}
          csvName="demo-funnel"
          columns={[
            { key: "step", label: t("adminApp.demo.step") },
            { key: "count", label: t("adminApp.kit.count"), numeric: true },
          ]}
          rows={steps.map((s) => ({ step: s.label, count: s.value }))}
        >
          <BarList items={steps} formatValue={format.count} empty={t("adminApp.chart.noValues")} />
        </ChartFrame>
        <BucketsFigure
          title={t("adminApp.demo.minutesToOpen")}
          description={t("adminApp.demo.minutesToOpenHint")}
          counts={data.summary.minutesToOpen}
          edges={DEMO_OPEN_EDGES_MINUTES}
          formatEdge={(n) => format.duration(n * 60)}
          continuous
          csvName="demo-minutes-to-open"
        />
      </Grid>
    </div>
  );
}

function Engagement({ data }: { data: Outcomes }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const tier = (value: string) => t(`adminApp.demo.tiers.${value as "explored"}`);
  const series = data.tiers.map((row) => ({
    key: row.tier,
    label: tier(row.tier),
    points: row.counts,
  }));
  const choices: {
    key: string;
    title: string;
    rows: DemoSummary["byLanguage"];
    labelOf: (v: string) => string;
  }[] = [
    {
      key: "example",
      title: t("adminApp.demo.byExampleData"),
      rows: data.summary.byExampleData,
      labelOf: (v) => t(`adminApp.demo.exampleData.${v as "example"}`),
    },
    {
      key: "language",
      title: t("adminApp.demo.byLanguage"),
      rows: data.summary.byLanguage,
      labelOf: languageLabel(t),
    },
    {
      key: "device",
      title: t("adminApp.demo.byDevice"),
      rows: data.summary.byDevice,
      labelOf: deviceLabel(t),
    },
  ];
  return (
    <div className="space-y-4">
      <ChartFrame
        title={t("adminApp.demo.tiersOverTime")}
        csvName="demo-tiers"
        columns={[
          {
            key: "period",
            label: t("adminApp.health.period"),
            format: (v) => format.period(String(v)),
          },
          ...data.tiers.map((row) => ({ key: row.tier, label: tier(row.tier), numeric: true })),
        ]}
        rows={data.periods.map((period, i) => ({
          period,
          ...Object.fromEntries(data.tiers.map((row) => [row.tier, row.counts[i] ?? 0])),
        }))}
      >
        <TimeSeries
          periods={data.periods}
          series={series}
          formatValue={format.count}
          formatPeriod={format.period}
          label={t("adminApp.demo.tiersOverTime")}
          height={180}
          integer
        />
      </ChartFrame>
      {/* Four tiers and two counts need the full width. */}
      {choices.map((choice) => (
        <ChartFrame
          key={choice.key}
          title={choice.title}
          description={t("adminApp.demo.byChoiceHint")}
          csvName={`demo-tiers-by-${choice.key}`}
          columns={[
            { key: "value", label: t("adminApp.kit.value") },
            { key: "count", label: t("adminApp.kit.count"), numeric: true },
            ...DEMO_TIERS.map((tierKey) => ({
              key: tierKey,
              label: tier(tierKey),
              numeric: true,
            })),
          ]}
          rows={choice.rows.map((row) => ({
            value: row.value,
            count: row.count,
            ...Object.fromEntries(DEMO_TIERS.map((tierKey, i) => [tierKey, row.tiers[i] ?? 0])),
          }))}
        >
          <HeatTable
            caption={choice.title}
            rowHeader={t("adminApp.kit.value")}
            compact
            columns={DEMO_TIERS.map((tierKey) => ({ key: tierKey, label: tier(tierKey) }))}
            rows={choice.rows.map((row): HeatRow => ({
              key: row.value,
              label: choice.labelOf(row.value),
              detail: format.count(row.count),
              cells: row.tiers.map((n) => ({
                share: row.count ? n / row.count : 0,
                text: format.count(n),
              })),
            }))}
            detailHeader={t("adminApp.demo.started")}
            legend={["0", t("adminApp.demo.allOfRow")]}
          />
        </ChartFrame>
      ))}
    </div>
  );
}

function Time({ summary }: { summary: DemoSummary }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  return (
    <Grid columns={3}>
      <BucketsFigure
        title={t("adminApp.demo.activeTime")}
        counts={summary.activeMinutes}
        edges={DEMO_ACTIVE_EDGES_MINUTES}
        formatEdge={(n) => format.duration(n * 60)}
        continuous
        csvName="demo-active-time"
      />
      <BucketsFigure
        title={t("adminApp.demo.visits")}
        counts={summary.visits}
        edges={DEMO_VISIT_EDGES}
        formatEdge={(n) => format.count(n)}
        csvName="demo-visits"
      />
      <BucketsFigure
        title={t("adminApp.demo.span")}
        description={t("adminApp.demo.spanHint")}
        counts={summary.spanHours}
        edges={DEMO_LIFETIME_EDGES_HOURS}
        formatEdge={String}
        continuous
        csvName="demo-span"
      />
    </Grid>
  );
}

function Ended({ data }: { data: Outcomes }) {
  const { t } = useTranslation();
  return (
    <Grid>
      <TallyFigure
        title={t("adminApp.demo.endedBy")}
        rows={data.endedBy}
        csvName="demo-ended-by"
        labelOf={(v) =>
          (DEMO_END_REASONS as readonly string[]).includes(v)
            ? t(`adminApp.demo.endReasons.${v as DemoEndReason}`)
            : v
        }
        base={data.total}
      />
      <BucketsFigure
        title={t("adminApp.demo.endedByVisitor")}
        description={t("adminApp.demo.endedByVisitorHint")}
        counts={data.summary.endedByVisitorHours}
        edges={DEMO_LIFETIME_EDGES_HOURS}
        formatEdge={String}
        continuous
        csvName="demo-ended-by-visitor"
      />
    </Grid>
  );
}

function Played({ summary }: { summary: DemoSummary }) {
  const { t } = useTranslation();
  const area = areaLabel(t);
  const started = summary.steps.started;
  return (
    <Grid columns={3}>
      <TallyFigure
        title={t("adminApp.demo.areasRead")}
        rows={summary.areasRead}
        csvName="demo-areas-read"
        labelOf={area}
        base={started}
      />
      <TallyFigure
        title={t("adminApp.demo.areasChanged")}
        rows={summary.areasChanged}
        csvName="demo-areas-changed"
        labelOf={area}
        base={started}
      />
      <TallyFigure
        title={t("adminApp.demo.firstAreaChanged")}
        rows={summary.firstAreaChanged}
        csvName="demo-first-area"
        labelOf={area}
      />
    </Grid>
  );
}

function Capacity() {
  const { t } = useTranslation();
  const format = useAdminFormat();
  return (
    <div className="space-y-4">
      <ReportView>
        {(report) => (
          <Stats
            items={[
              {
                key: "live",
                label: t("adminApp.demo.live"),
                value: format.count(report.demo?.live),
              },
              { key: "max", label: t("adminApp.demo.max"), value: format.count(report.demo?.max) },
            ]}
          />
        )}
      </ReportView>
      <Grid>
        <HistoryFigure
          title={t("adminApp.demo.liveOverTime")}
          figures={[{ figure: "demo.live", label: t("adminApp.demo.live") }]}
          csvName="demo-live"
        />
        <CountsFigure
          title={t("adminApp.demo.busy")}
          description={t("adminApp.demo.busyHint")}
          source="usage"
          name="demo.busy"
          csvName="demo-busy"
        />
      </Grid>
    </div>
  );
}

const languageLabel = (t: TFunction) => (v: string) =>
  v === "en" || v === "de"
    ? t(`adminApp.languages.${v}`)
    : v === "unknown"
      ? t("adminApp.kit.notKnown")
      : v;

const deviceLabel = (t: TFunction) => (v: string) =>
  ["phone", "tablet", "desktop", "unknown"].includes(v) ? t(`adminApp.devices.${v as "phone"}`) : v;

/** The demo on the Overview: the way in and the tiers of the last 30 days, linking to its page. */
export function DemoCard({ installation }: { installation: string }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const trpc = useTRPC();
  const query = useQuery(trpc.demo.outcomes.queryOptions({ name: installation, range: "30d" }));
  const steps = useSteps();
  const href = `/demo?installation=${encodeURIComponent(installation)}&range=30d`;
  return (
    <QueryView query={query}>
      {(data) => (
        <ChartFrame
          title={t("adminApp.demo.card")}
          description={t("adminApp.demo.cardHint")}
          csvName="demo-last-30-days"
          columns={[
            { key: "step", label: t("adminApp.demo.step") },
            { key: "count", label: t("adminApp.kit.count"), numeric: true },
          ]}
          rows={DEMO_STEPS.map((step) => ({ step, count: data.summary.steps[step] }))}
          aside={
            <a
              href={href}
              onClick={(event) => {
                event.preventDefault();
                navigate(href);
              }}
              className="inline-flex items-center gap-1 text-sm font-medium text-brand-fg hover:underline"
            >
              {t("adminApp.demo.open")}
              <ArrowRightIcon className="size-3.5" aria-hidden />
            </a>
          }
        >
          <div className="grid grid-cols-2 gap-6">
            <BarList
              items={steps(data.summary)}
              formatValue={format.count}
              empty={t("adminApp.chart.noValues")}
            />
            <BarList
              items={data.tiers.map((row) => ({
                key: row.tier,
                label: t(`adminApp.demo.tiers.${row.tier as "explored"}`),
                value: row.counts.reduce((sum, n) => sum + n, 0),
              }))}
              formatValue={format.count}
              empty={t("adminApp.chart.noValues")}
            />
          </div>
        </ChartFrame>
      )}
    </QueryView>
  );
}
